import { BASE_API, ejecutarEnlace, type Metodo, type PeticionReal } from './enlace'
import { ErrorApi, interpretarError } from './errores'

export type ObtenerToken = () => Promise<string | null>

export interface OpcionesPeticion {
  metodo?: Metodo
  parametros?: Record<string, string | number | undefined | null>
  /**
   * Cuerpo JSON. Lo necesitan `/api/agendamiento/resolver` y `/publicar`, que
   * reciben la lista entera de clientes: no cabe en la query string y ademas no
   * es un filtro. Se deja opcional porque hay POST sin cuerpo, como
   * `/api/clientes/{id}/geocodificar`.
   */
  cuerpo?: unknown
  senal?: AbortSignal
}

function construirUrl(ruta: string, parametros?: OpcionesPeticion['parametros']): string {
  if (!parametros) return ruta
  const consulta = new URLSearchParams()
  for (const [clave, valor] of Object.entries(parametros)) {
    // Se omiten vacios para no enviar `?q=` cuando el buscador esta en blanco:
    // el backend lo trataria como un filtro de cadena vacia.
    if (valor === undefined || valor === null || valor === '') continue
    consulta.set(clave, String(valor))
  }
  const cadena = consulta.toString()
  return cadena ? `${ruta}?${cadena}` : ruta
}

/**
 * `alCaducar`: se llama cuando el backend responde 401 a una peticion que iba
 * con token (caducado antes de tiempo, revocado, usuario desactivado o clave
 * rotada). La sesion lo usa para cerrar y volver al login en vez de dejar la
 * interfaz "dentro" con todas las pantallas en error.
 */
export function crearClienteApi(obtenerToken: ObtenerToken, alCaducar?: () => void) {
  /** Una llamada HTTP al backend real; `ruta` es relativa a BASE_API (/api/v1). */
  async function pedirReal(ruta: string, opciones: PeticionReal = {}): Promise<unknown> {
    const token = await obtenerToken()
    const cabeceras = new Headers({ Accept: 'application/json' })
    if (token) cabeceras.set('Authorization', `Bearer ${token}`)

    // La cabecera solo se pone cuando hay cuerpo: anunciar un JSON que no se
    // manda haria que algunos servidores intentaran parsear el vacio.
    const lleva = opciones.cuerpo !== undefined
    if (lleva) cabeceras.set('Content-Type', 'application/json')

    const respuesta = await fetch(construirUrl(`${BASE_API}${ruta}`, opciones.parametros), {
      method: opciones.metodo ?? 'GET',
      headers: cabeceras,
      body: lleva ? JSON.stringify(opciones.cuerpo) : undefined,
      signal: opciones.senal,
    })

    if (!respuesta.ok) {
      const error = await interpretarError(respuesta)
      if (token && error.esSesionCaducada) alCaducar?.()
      throw error
    }
    // Los PDF (albarán, ruta del día) llegan como fichero, no como JSON.
    if (opciones.formato === 'archivo') return await respuesta.blob()
    // 204 o cuerpo vacío (p. ej. `POST /users/:id/password`): no hay JSON que leer.
    const texto = await respuesta.text()
    return texto ? JSON.parse(texto) : null
  }

  /**
   * Pide una ruta lógica de la interfaz (`/api/clientes`...). `enlace.ts` la
   * traduce a la ruta real de trazabis-agendamiento y devuelve la respuesta con
   * la forma que esperan los componentes.
   */
  async function pedir<T>(ruta: string, opciones: OpcionesPeticion = {}): Promise<T> {
    return (await ejecutarEnlace(pedirReal, ruta, opciones)) as T
  }

  /** Igual que `pedir`, pero la respuesta es un fichero (PDF). */
  async function pedirArchivo(ruta: string, opciones: OpcionesPeticion = {}): Promise<Blob> {
    return (await ejecutarEnlace(pedirReal, ruta, { ...opciones, formato: 'archivo' })) as Blob
  }

  return { pedir, pedirArchivo }
}

export type ClienteApi = ReturnType<typeof crearClienteApi>
export { ErrorApi }
